"use client";

import type { useChat } from "@ai-sdk/react";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import {
  AlertCircleIcon,
  CheckIcon,
  ChevronsUpDownIcon,
  ExternalLinkIcon,
  FrownIcon,
  Loader2Icon,
} from "lucide-react";
import Link from "next/link";
import { useParams } from "next/navigation";
import * as React from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { AgentUIMessage } from "@/lib/ai/types";
import { useRouter } from "@/lib/navigation";
import { cn } from "@/lib/utils";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import { ADD_SEARCH_CONSOLE_PROPERTY_NAME } from "./constants";
import type { AddSearchConsolePropertyInput } from "./schema";

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

type AvailablePropertyOption = {
  property: string;
  email: string;
  type: "USER" | "SERVICE_ACCOUNT";
  serviceAccountId?: string;
};

function encodePropertyOptionValue(
  option: Pick<
    AvailablePropertyOption,
    "property" | "type" | "serviceAccountId"
  >,
): string {
  return `${option.property}___${option.type}___${option.serviceAccountId ?? ""}`;
}

function parsePropertyOptionValue(value: string): {
  property: string;
  type: "USER" | "SERVICE_ACCOUNT";
  serviceAccountId?: string;
} | null {
  if (!value) return null;
  const parts = value.split("___");
  if (parts.length < 3) return null;

  const type = parts.at(-2);
  if (type !== "USER" && type !== "SERVICE_ACCOUNT") return null;

  const property = parts.slice(0, -2).join("___");
  const serviceAccountIdRaw = parts.at(-1) ?? "";
  const serviceAccountId =
    type === "SERVICE_ACCOUNT" ? serviceAccountIdRaw || undefined : undefined;

  return { property, type, serviceAccountId };
}

const AvailablePropertiesQuery = graphql(`
  query AvailablePropertiesForAddScPropertyTool($workspaceId: ID!) {
    viewer {
      id
      __typename
      email
      availableProperties(organizationId: $workspaceId) {
        __typename
        property
      }
    }

    workspace: node(id: $workspaceId) {
      ... on Organization {
        id
        __typename
        serviceAccounts {
          id
          email
          availableProperties {
            property
          }
        }
      }
    }
  }
`);

function AvailablePropertyCombobox({
  id,
  options,
  value,
  onValueChange,
  placeholder,
  searchPlaceholder,
  disabled,
}: {
  id?: string;
  options: ReadonlyArray<AvailablePropertyOption>;
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = React.useState(false);

  const selectedOption = React.useMemo(() => {
    return options.find(
      (option) => encodePropertyOptionValue(option) === value,
    );
  }, [options, value]);

  const customFilter = React.useCallback(
    (itemValue: string, search: string) => {
      const searchLower = search.toLowerCase();
      const valueLower = itemValue.toLowerCase();

      if (valueLower.includes(searchLower)) {
        if (valueLower.startsWith(searchLower)) {
          return 2;
        }
        return 1;
      }
      return 0;
    },
    [],
  );

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled}
        >
          <span className="truncate">
            {selectedOption ? (
              <div className="flex flex-row gap-4 items-center">
                <span className="truncate">{selectedOption.property}</span>
                <span className="text-xs text-muted-foreground truncate">
                  {selectedOption.email}
                </span>
              </div>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </span>
          <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command filter={customFilter}>
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList className="max-h-[350px] overflow-hidden">
            <CommandEmpty>
              <Trans>No property found.</Trans>
            </CommandEmpty>
            <ScrollArea className="max-h-[300px]">
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={`${option.property}-${option.email}-${option.type}-${option.serviceAccountId ?? ""}`}
                    value={`${option.property} ${option.email}`}
                    onSelect={() => {
                      onValueChange(encodePropertyOptionValue(option));
                      setOpen(false);
                    }}
                  >
                    <div className="flex flex-col flex-1">
                      <span>{option.property}</span>
                      {option.email && (
                        <span className="text-xs text-muted-foreground">
                          {option.email}
                        </span>
                      )}
                    </div>
                    <span
                      className={cn(
                        "ml-auto h-4 w-4",
                        value === `${option.property}___${option.type}`
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    >
                      <CheckIcon className="h-4 w-4" />
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function AddSearchConsolePropertyConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const { t } = useLingui();
  const router = useRouter();
  const params = useParams();
  const workspaceIdFromParams =
    typeof params.workspaceId === "string"
      ? decodeURIComponent(params.workspaceId)
      : "";

  const isTarget =
    isToolUIPart(part) &&
    getToolName(part) === ADD_SEARCH_CONSOLE_PROPERTY_NAME;
  const isInputAvailable = part.state === "input-available";
  const input = (part.input ?? {}) as Partial<AddSearchConsolePropertyInput>;

  const [{ data, fetching, error }] = useQuery({
    query: AvailablePropertiesQuery,
    variables: { workspaceId: workspaceIdFromParams },
    requestPolicy: "network-only",
    pause: !(isTarget && isInputAvailable && workspaceIdFromParams),
  });

  const propertyOptions = React.useMemo(() => {
    const viewerProperties =
      data?.viewer?.availableProperties?.map((p) => ({
        property: p.property,
        email: data?.viewer?.email ?? "",
        type: "USER" as const,
        serviceAccountId: undefined,
      })) ?? [];

    const serviceAccountProperties =
      data?.workspace?.__typename === "Organization"
        ? data.workspace.serviceAccounts.flatMap((sa) =>
            sa.availableProperties.map((p) => ({
              property: p.property,
              email: sa.email,
              type: "SERVICE_ACCOUNT" as const,
              serviceAccountId: sa.id,
            })),
          )
        : [];

    return [...viewerProperties, ...serviceAccountProperties];
  }, [data]);

  const [selectedProperty, setSelectedProperty] = React.useState<string>(() => {
    if (typeof input.property === "string" && typeof input.type === "string") {
      const type = input.type;
      if (type !== "USER" && type !== "SERVICE_ACCOUNT") return "";

      const serviceAccountId =
        typeof input.serviceAccountId === "string"
          ? input.serviceAccountId
          : "";
      return encodePropertyOptionValue({
        property: input.property,
        type,
        serviceAccountId,
      });
    }
    return "";
  });

  React.useEffect(() => {
    if (
      !selectedProperty &&
      typeof input.property === "string" &&
      typeof input.type === "string"
    ) {
      const type = input.type;
      if (type !== "USER" && type !== "SERVICE_ACCOUNT") return;

      const serviceAccountId =
        typeof input.serviceAccountId === "string"
          ? input.serviceAccountId
          : "";
      setSelectedProperty(
        encodePropertyOptionValue({
          property: input.property,
          type,
          serviceAccountId,
        }),
      );
    }
  }, [input.property, input.serviceAccountId, input.type, selectedProperty]);

  const selected = React.useMemo(
    () => parsePropertyOptionValue(selectedProperty),
    [selectedProperty],
  );
  const canSubmit =
    Boolean(selected?.property) &&
    (selected?.type === "USER" || Boolean(selected?.serviceAccountId));

  const isNoProperties = propertyOptions.length === 0;
  const propertyFieldId = React.useId();

  if (!isTarget || !isInputAvailable) return null;

  return (
    <div className="p-4 text-sm space-y-4">
      <div className="font-medium">
        <Trans>Add Search Console property</Trans>
      </div>

      {fetching ? (
        <div className="flex justify-center py-4">
          <Loader2Icon className="w-4 h-4 animate-spin" />
        </div>
      ) : isNoProperties ? (
        <Alert variant="default">
          <FrownIcon className="w-4 h-4" />
          <AlertTitle>
            <Trans>No available properties to add</Trans>
          </AlertTitle>
          <AlertDescription>
            <Trans>
              Either all properties have already been added to this workspace,
              or no properties were found in your account.
            </Trans>
            <Button asChild variant="link" className="px-0">
              <Link
                href="https://search.google.com/search-console/not-verified"
                target="_blank"
                rel="noreferrer"
              >
                <Trans>Google Search Console</Trans>
                <ExternalLinkIcon className="w-4 h-4" />
              </Link>
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        <div className="max-w-full overflow-hidden space-y-2">
          <div className="text-muted-foreground">
            <Trans>
              You can add properties you own, as well as properties owned by
              service accounts that have been added to the workspace.
            </Trans>
          </div>
          <div>
            <Label htmlFor={propertyFieldId}>
              <Trans>Property</Trans>
            </Label>
            <AvailablePropertyCombobox
              id={propertyFieldId}
              options={propertyOptions}
              value={selectedProperty}
              onValueChange={setSelectedProperty}
              placeholder={t`Select a property`}
              searchPlaceholder={t`Search or add new...`}
              disabled={isNoProperties}
            />
          </div>
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircleIcon className="w-4 h-4" />
          <AlertTitle>
            <Trans>Error</Trans>
          </AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}

      <div className="flex gap-2">
        {canSubmit ? (
          <ApproveButton
            toolCallId={part.toolCallId}
            toolName={getToolName(part)}
            addToolResult={addToolResult}
            sendMessage={sendMessage}
            onSuccess={() => router.refresh()}
            className="w-24"
            overrideInput={{
              workspaceId: workspaceIdFromParams,
              property: selected?.property,
              type: selected?.type,
              ...(selected?.type === "SERVICE_ACCOUNT"
                ? { serviceAccountId: selected.serviceAccountId }
                : {}),
            }}
          >
            <Trans>Add</Trans>
          </ApproveButton>
        ) : (
          <Button size="sm" className="w-24" disabled>
            <Trans>Add</Trans>
          </Button>
        )}
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
